import { BillType, DiscountInfo, FullBillingInfo } from '../all-types'

const buildEmptyBillingInfo = (): FullBillingInfo => ({
  totalItemsCostRaw: 0,
  totalDiscountIssued: 0,
  totalTaxCharged: 0,
  TaxChargeSheetView: {},
  finalBillAmount: 0,
  extraInfo: { roundOff: '0.00' },
})

const normalizeDiscountsInfo = (discountsInfo: unknown): DiscountInfo[] => {
  if (Array.isArray(discountsInfo)) {
    return discountsInfo
  }

  if (discountsInfo && typeof discountsInfo === 'object') {
    return [discountsInfo as DiscountInfo]
  }

  return []
}

const normalizeBillDiscountsInfo = (bill: BillType): BillType => ({
  ...bill,
  items: (bill.items || []).map((item) => ({
    ...item,
    billingInfo: {
      ...item.billingInfo,
      discounts: {
        ...item.billingInfo.discounts,
        discountsInfo: normalizeDiscountsInfo(
          item.billingInfo.discounts?.discountsInfo
        ),
      },
    },
  })),
})

/**
 * Aggregates per-item billing info into a `FullBillingInfo`, including
 * `extraInfo.roundOff` and the tax/discount display labels used by the
 * receipt printer. Safe to call with bills loaded from the backend that
 * do not have `extraInfo` persisted.
 */
const calculateFullBillingInfo = (fullBill: BillType): FullBillingInfo => {
  const tempBillingInfo = buildEmptyBillingInfo()
  try {
    const { items } = normalizeBillDiscountsInfo(fullBill)
    items?.forEach((item) => {
      tempBillingInfo.totalItemsCostRaw += item.billingInfo.rawAmount
      tempBillingInfo.totalDiscountIssued += item.billingInfo.totalDiscountGiven
      tempBillingInfo.totalTaxCharged += item.billingInfo.totalTaxCharged

      if (item.billingInfo.totalTaxCharged > 0) {
        item.billingInfo.tax?.taxInfo?.forEach((tax) => {
          const taxName = tax.taxComponent.taxName
          if (tempBillingInfo.TaxChargeSheetView[taxName]) {
            tempBillingInfo.TaxChargeSheetView[taxName] += tax.taxAmount
          } else {
            tempBillingInfo.TaxChargeSheetView[taxName] = tax.taxAmount
          }
          tempBillingInfo.extraInfo![taxName] =
            `${tax.taxComponent.taxName} ${tax.taxComponent.applicableTax}%`
        })

        item.billingInfo.discounts.discountsInfo.forEach((discount) => {
          const discountName = discount.discountComponent.discountName
          tempBillingInfo.extraInfo!['discount'] =
            `${discountName ?? 'discount'} ${discount.discountComponent.applicableDiscount}%`
          tempBillingInfo.extraInfo!['discountPercentage'] =
            discount.discountComponent.applicableDiscount.toString()
          tempBillingInfo.extraInfo!['discountName'] = discountName
        })
      }

      tempBillingInfo.finalBillAmount = Number(
        (
          tempBillingInfo.finalBillAmount + item.billingInfo.finalAmount
        ).toFixed(2)
      )
    })

    const finalBillAmountWithDecimals = Number(
      tempBillingInfo.finalBillAmount.toFixed(2)
    )
    const roundedFinalBillAmount = Math.floor(finalBillAmountWithDecimals)

    tempBillingInfo.finalBillAmount = roundedFinalBillAmount
    tempBillingInfo.extraInfo!.roundOff = (
      finalBillAmountWithDecimals - roundedFinalBillAmount
    ).toFixed(2)
  } catch (e) {
    console.error(e)
  }
  return tempBillingInfo
}

/**
 * Returns the bill's existing `billingInfo` when `extraInfo` is already
 * populated (e.g. the in-cart bill), otherwise recomputes it from items so
 * fields like `roundOff` are never `undefined` (e.g. bills fetched from the
 * backend whose schema does not yet persist `extraInfo`).
 */
const ensureFullBillingInfo = (bill: BillType): FullBillingInfo => {
  if (bill?.billingInfo?.extraInfo?.roundOff !== undefined) {
    return bill.billingInfo
  }
  return calculateFullBillingInfo(bill)
}

export {
  calculateFullBillingInfo,
  ensureFullBillingInfo,
  normalizeBillDiscountsInfo,
  normalizeDiscountsInfo,
}
